/**
 * The Profile tab (Robert, 14.10.2026): the business description and the chat window's welcome messages are
 * edited in place with the platform's MUI fields and saved together with the Save button under the tab, which
 * stays in view while the fields above it scroll. Leaving with unsaved texts asks first (the platform's
 * unsaved-changes guard); a change Mo makes meanwhile comes in unless the owner is editing.
 * The settings below them (intelligence level, model, thoroughness, automatic check) save at once when picked
 * (Robert, 16.10.2026), a dearer intelligence level after its confirmation dialog.
 * "Regenerate from knowledge" under the Business Context (Robert, 02.11.2026) puts a text written from the agent's
 * current knowledge into the field; the owner checks it and saves it like any edit. What came of it shows right
 * under the field.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { faCheck, faPlus, faTrash, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { Button, CircularProgress, FormControl, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { useOptionalUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import useGenerateBusinessContext, { generateMessage } from '../../hooks/useGenerateBusinessContext';
import { guardWrite } from '../../utils/permissions';
import { AnswerLevelPicker } from './CreditLevels';
import { studioApi } from './studioApi';
import { MUI_CONTAINED, MUI_FIELD, Spinner, Toggle } from './ui';

const FIELD = { ...MUI_FIELD, '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } };
const HELP = { component: 'div', sx: { mx: 0, mt: 0.5 } };
const SAVED_MS = 4000;

function startOf(spec) {
  return {
    description: spec?.company_description || '',
    greetings: spec?.greetings?.length ? [...spec.greetings] : [''],
  };
}
const SETTING_KEYS = { effort: 'effort', verify: 'verify_answers', model: 'model' };
const clean = (d) => ({ ...d, description: d.description.trim(), greetings: d.greetings.map((g) => g.trim()).filter(Boolean) });
const keyOf = (d) => JSON.stringify(clean(d));

export function useProfileForm({ botId, spec, setSpec, limits }) {
  const { t, i18n } = useTranslation();
  const start = startOf(spec);
  const startKey = keyOf(start);
  const [draft, setDraft] = useState(start);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [failed, setFailed] = useState('');
  const [pending, setPending] = useState({}); // settings being saved, shown until the spec has them
  const seen = useRef({ bot: botId, key: startKey }); // the saved state the draft started from
  const gen = useGenerateBusinessContext(botId, (i18n.language || 'en').slice(0, 2));

  useEffect(() => {
    const before = seen.current;
    if (before.bot === botId && before.key === startKey) return;
    // another agent: its own values; a change from elsewhere: taken over unless the owner is editing
    setDraft((d) => (before.bot !== botId || keyOf(d) === before.key ? startOf(spec) : d));
    seen.current = { bot: botId, key: startKey };
  }, [botId, startKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!saved) return undefined;
    const id = setTimeout(() => setSaved(false), SAVED_MS);
    return () => clearTimeout(id);
  }, [saved]);

  const a = clean(draft);
  const b = clean(start);
  const dirty = keyOf(draft) !== startKey;
  const descChanged = a.description !== b.description;
  const greetChanged = JSON.stringify(a.greetings) !== JSON.stringify(b.greetings);
  const descOver = a.description.length > limits.business_context;
  const greetOver = draft.greetings.some((g) => g.trim().length > limits.welcome_message);
  const greetMissing = greetChanged && !!spec?.has_chat_window && !a.greetings.length;
  const invalid = (descChanged && descOver) || greetOver || greetMissing;

  const clearGenerated = gen.clear;
  const set = useCallback((patch) => {
    setSaved(false);
    setFailed('');
    clearGenerated();
    setDraft((d) => ({ ...d, ...(typeof patch === 'function' ? patch(d) : patch) }));
  }, [clearGenerated]);

  // "Regenerate from knowledge": only over a saved text, so nothing the owner typed is lost
  const generate = async () => {
    if (descChanged || gen.running || !guardWrite()) return;
    setSaved(false);
    setFailed('');
    const r = await gen.generate();
    if (r?.status === 'ok' && r.business_context) setDraft((d) => ({ ...d, description: r.business_context }));
  };

  const save = async () => {
    if (!dirty || invalid || saving || !guardWrite()) return false;
    setSaving(true);
    setFailed('');
    try {
      const profile = {};
      if (descChanged) profile.company_description = a.description;
      if (greetChanged) profile.greetings = a.greetings;
      if (Object.keys(profile).length) setSpec(await studioApi.patch(botId, '/profile', profile));
      setDraft((d) => ({ ...d, description: a.description, greetings: a.greetings.length ? a.greetings : [''] }));
      setSaved(true);
      gen.clear();
      return true;
    } catch (e) {
      setFailed(e.status === 402 ? t('agent_studio_level_locked') : (e.message || String(e)));
      return false;
    } finally {
      setSaving(false);
    }
  };
  const discard = () => { setDraft(startOf(spec)); setFailed(''); gen.clear(); return true; };

  // the settings save at once, without the Save button
  const settings = {
    effort: spec?.settings?.effort || 'medium', verify: !!spec?.settings?.verify_answers, model: spec?.model || '', ...pending,
  };
  const saveSetting = async (patch) => {
    if (!guardWrite()) return;
    setPending((p) => ({ ...p, ...patch }));
    setSaved(false);
    setFailed('');
    try {
      const body = Object.fromEntries(Object.entries(patch).map(([k, v]) => [SETTING_KEYS[k], v]));
      setSpec(await studioApi.patch(botId, '/settings', body));
      setSaved(true);
    } catch (e) {
      setFailed(e.message || String(e));
    } finally {
      // a newer pick of the same setting stays until its own answer
      setPending((p) => Object.fromEntries(Object.entries(p).filter(([k, v]) => !(k in patch) || v !== patch[k])));
    }
  };
  const settingSaved = useCallback(() => { setFailed(''); setSaved(true); }, []);

  // the platform asks before leaving the page with unsaved changes (Save / Discard)
  const guard = useOptionalUnsavedChanges();
  const register = guard?.registerUnsavedChangesGuard;
  const handlers = useRef({ save, discard });
  handlers.current = { save, discard };
  useEffect(() => {
    if (!register) return undefined;
    return register(`agent-studio-profile-${botId}`, {
      when: dirty, onSave: () => handlers.current.save(), onDiscard: () => handlers.current.discard(),
    });
  }, [register, botId, dirty]);

  return {
    draft, set, save, discard, dirty, invalid, saving, saved, failed, limits, settings, saveSetting, settingSaved,
    descOver, greetMissing, initialDescriptionLength: (spec?.company_description || '').length,
    descChanged, generate, generating: gen.running, generated: generateMessage(t, gen.result),
  };
}

function Help({ note, count }) {
  return <span className="as-help"><span>{note}</span>{count && <span className="as-help-count">{count}</span>}</span>;
}

/** Under the Business Context, left of its count. Over unsaved changes it waits for Save or Discard (its tooltip
 *  says so; the wrapper carries it, a disabled button shows none). */
function GenerateContext({ form }) {
  const { t } = useTranslation();
  const blocked = form.descChanged && !form.generating;
  return (
    <span className="as-gen-context" title={t(blocked ? 'business_context_generate_unsaved' : 'business_context_generate_hint')}>
      <button type="button" className="as-btn-link as-small" disabled={blocked || form.generating} onClick={form.generate}>
        {form.generating ? <Spinner size={11} /> : <FontAwesomeIcon icon={faWandMagicSparkles} />}
        {t(form.generating ? 'business_context_generating' : 'business_context_generate')}
      </button>
    </span>
  );
}

/** What came of it, right under the field: the new text to check and save, or why there is none. */
function GeneratedNote({ note }) {
  const { t } = useTranslation();
  if (!note) return null;
  return (
    <div className={`as-gen-note ${note.tone === 'ok' ? 'is-ok' : 'is-warn'}`} role="status">
      <FontAwesomeIcon icon={faWandMagicSparkles} className="as-gen-note-ic" />
      <span>
        {note.text}
        {note.knowledge && <> <Link to="/knowledge/website-links" className="as-link">{t('agent_studio_knowledge_manage')}</Link></>}
      </span>
    </div>
  );
}

/** The fields (in the panel's scroll area). */
export function ProfileFields({ form, spec, botId, creditsActive }) {
  const { t, i18n } = useTranslation();
  const { draft, set, limits, settings, saveSetting } = form;
  const num = (n) => n.toLocaleString(i18n.language);
  const desc = draft.description.trim().length;
  const max = limits.business_context;
  const many = draft.greetings.length > 1;
  const setGreeting = (i, v) => set((d) => ({ greetings: d.greetings.map((x, j) => (j === i ? v : x)) }));

  return (
    <div className="as-profile-form">
      <TextField fullWidth multiline minRows={4} maxRows={4} variant="outlined" sx={FIELD}
        label={t('agent_studio_company_description')} value={draft.description}
        onChange={(e) => set({ description: e.target.value })}
        inputProps={{ maxLength: Math.max(max, form.initialDescriptionLength) }}
        error={form.descOver} FormHelperTextProps={HELP}
        helperText={<Help count={`${num(desc)}/${num(max)}`}
          note={form.descOver ? t('agent_studio_description_too_long', { count: num(desc), max: num(max) })
            : <GenerateContext form={form} />} />} />
      <GeneratedNote note={form.generated} />

      <div className="as-profile-group">
        {!spec.has_chat_window ? (
          <p className="as-small as-muted">{t('agent_studio_no_chat_window')}</p>
        ) : (
          <>
            {draft.greetings.map((g, i) => {
              const len = g.trim().length;
              return (
                <div key={i} className="as-greeting-row">
                  <TextField fullWidth multiline minRows={2} maxRows={6} variant="outlined" sx={FIELD}
                    label={many ? `${t('agent_studio_greeting')} ${i + 1}` : t('agent_studio_greeting')} value={g}
                    onChange={(e) => setGreeting(i, e.target.value)}
                    inputProps={{ maxLength: Math.max(limits.welcome_message, (spec.greetings?.[i] || '').length) }}
                    error={len > limits.welcome_message || (form.greetMissing && i === 0)} FormHelperTextProps={HELP}
                    helperText={<Help count={`${len}/${limits.welcome_message}`}
                      note={form.greetMissing && i === 0 ? t('agent_studio_greetings_required') : ''} />} />
                  {many && (
                    <IconButton size="small" className="as-greeting-remove" aria-label={t('remove')} title={t('remove')}
                      onClick={() => set((d) => ({ greetings: d.greetings.filter((_, j) => j !== i) }))}>
                      <FontAwesomeIcon icon={faTrash} />
                    </IconButton>
                  )}
                </div>
              );
            })}
            {draft.greetings.length < limits.welcome_messages && (
              <button type="button" className="as-btn-link as-small as-greeting-add"
                onClick={() => set((d) => ({ greetings: [...d.greetings, ''] }))}>
                <FontAwesomeIcon icon={faPlus} /> {t('agent_studio_greeting_add')}
              </button>
            )}
          </>
        )}
      </div>

      <div className="as-panel-subhead">{t('agent_studio_settings')}</div>
      {/* AI credits on: the intelligence level (1 / 4 / 8 credits per answer) decides the model */}
      <AnswerLevelPicker botId={botId} onSaved={form.settingSaved} />
      <div className="as-profile-settings">
        {!creditsActive && (
          <FormControl fullWidth size="small" sx={FIELD}>
            <InputLabel id="as-profile-model">{t('agent_studio_model')}</InputLabel>
            <Select labelId="as-profile-model" label={t('agent_studio_model')} value={settings.model}
              onChange={(e) => saveSetting({ model: e.target.value })}>
              {(spec.models || []).map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        <FormControl fullWidth size="small" sx={FIELD}>
          <InputLabel id="as-profile-effort">{t('agent_studio_effort')}</InputLabel>
          <Select labelId="as-profile-effort" label={t('agent_studio_effort')} value={settings.effort}
            onChange={(e) => saveSetting({ effort: e.target.value })}>
            {['low', 'medium', 'high'].map((k) => <MenuItem key={k} value={k}>{t(`agent_studio_effort_${k}`)}</MenuItem>)}
          </Select>
        </FormControl>
        <label className="as-settings-toggle">
          <Toggle checked={settings.verify} onChange={(v) => saveSetting({ verify: v })} />
          <span>{t('agent_studio_verify_answers')}</span>
        </label>
      </div>
    </div>
  );
}

/**
 * Under the tab, always in view, one compact row (Robert, 16.10.2026): saved or failed on the left; with unsaved
 * texts Discard Changes stands left of the right-aligned Save (as on the platform's design page, without its note).
 */
export function ProfileSaveBar({ form }) {
  const { t } = useTranslation();
  const { dirty, invalid, saving, saved, failed, save, discard } = form;
  const status = failed ? ['is-error', t('agent_studio_action_failed', { error: failed })]
    : saved && !dirty ? ['is-saved', t('agent_studio_profile_saved')] : null;
  return (
    <div className="as-savebar">
      <span className={`as-savebar-status ${status ? status[0] : ''}`} role="status">
        {status && status[0] === 'is-saved' && <FontAwesomeIcon icon={faCheck} />}
        {status ? status[1] : ''}
      </span>
      {dirty && (
        <button type="button" className="as-savebar-discard" disabled={saving} onClick={discard}>{t('discard_changes')}</button>
      )}
      <Button variant="contained" sx={MUI_CONTAINED} disabled={!dirty || invalid || saving} onClick={save}
        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>
        {t('save')}
      </Button>
    </div>
  );
}
